import * as React from "react";
import { useRouter } from "next/router";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";

import SVG from "@/assets/svg";
import {
  CARE_PLANNING_REVIEW_REPORTS_FLAG,
  ALERT_FLAG,
  FINANCE_INVOICES_FLAG,
} from "@/config/releaseFlags";
import { useAppSelector } from "@/redux/hooks";
import { ACCESS_LEVEL } from "@/utils/constants";
import { convertCamelCaseToSentenceCase } from "@/utils/helpers";
import { useFeatureFlags } from "@/utils/providers/feature-flags-provider";

type Anchor = "left";

export default function MobileHeader() {
  const router = useRouter();
  const [state, setState] = React.useState({
    left: false,
  });

  const { terms } = useAppSelector((state) => state.terms);

  const { tenantSubscription } = useAppSelector((state) => state.terms);

  const { user } = useAppSelector((state) => state.user);
  const { isEnabled } = useFeatureFlags();

  const toggleDrawer =
    (anchor: Anchor, open: boolean) =>
    (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }

      setState({ ...state, [anchor]: open });
    };

  const allowedPages = React.useMemo(() => {
    if (!tenantSubscription) return ["staff", "service"];
    const allowedPages =
      tenantSubscription?.currentSub?.plan?.allowedPages || [];
    return [
      "staff",
      "service",
      "staff/staff-members",
      "service/service-user",
      ...allowedPages,
    ];
  }, [tenantSubscription]);

  const hasPermission = (optionId: string) => {
    return optionId === "staff"
      ? [ACCESS_LEVEL.ADMINISTRATOR].includes(user.accessLevel)
      : true;
  };

  const list = (anchor: Anchor) => (
    <Box
      sx={{ width: anchor === "left" || anchor === "bottom" ? "auto" : 350 }}
      role="presentation"
      onClick={toggleDrawer(anchor, false)}
      onKeyDown={toggleDrawer(anchor, false)}
    >
      <List>
        <ListItem>
          <Link href="/dashboard">
            <ListItemButton>
              <ListItemText>Dashboard</ListItemText>
            </ListItemButton>
          </Link>
        </ListItem>
        <Divider />

        {allowedPages.includes("staff") && hasPermission("staff") && (
          <>
            <ListItem>
              <Link href="/staff/staff-members">
                <ListItemButton>
                  <ListItemText>
                    {convertCamelCaseToSentenceCase(
                      terms.data?.staff || "Staff"
                    )}
                  </ListItemText>
                </ListItemButton>
              </Link>
            </ListItem>
            <Divider />
          </>
        )}

        {allowedPages.includes("service") && (
          <>
            <ListItem>
              <Link href="/service/service-user">
                <ListItemButton>
                  <ListItemText>
                    {convertCamelCaseToSentenceCase(
                      terms.data?.serviceUser || "Service User"
                    )}
                  </ListItemText>
                </ListItemButton>
              </Link>
            </ListItem>
            <Divider />
          </>
        )}

        <ListItem>
          <Link href="/dailyNote">
            <ListItemButton>
              <ListItemText>Daily Note</ListItemText>
            </ListItemButton>
          </Link>
        </ListItem>
        <Divider />
        <ListItem>
          <Link href="/tasks-management/to-do">
            <ListItemButton>
              <ListItemText>Task management</ListItemText>
            </ListItemButton>
          </Link>
        </ListItem>
        <Divider />
        <ListItem>
          <Link href="/roster">
            <ListItemButton>
              <ListItemText>Roster</ListItemText>
            </ListItemButton>
          </Link>
        </ListItem>
        <Divider />
        <ListItem>
          <Link href="/handover/handovers">
            <ListItemButton>
              <ListItemText>Handovers</ListItemText>
            </ListItemButton>
          </Link>
        </ListItem>
        <Divider />
        {allowedPages.includes("reports") &&
          isEnabled(CARE_PLANNING_REVIEW_REPORTS_FLAG, false) && (
            <>
              <ListItem>
                <Link href="/reports">
                  <ListItemButton>
                    <ListItemText>Reports</ListItemText>
                  </ListItemButton>
                </Link>
              </ListItem>
              <Divider />
            </>
          )}
        <ListItem>
          <Link href="/document">
            <ListItemButton>
              <ListItemText>Documents</ListItemText>
            </ListItemButton>
          </Link>
        </ListItem>
        {isEnabled(FINANCE_INVOICES_FLAG, false) && (
          <>
            <Divider />
            <ListItem>
              <Link href="/finance">
                <ListItemButton>
                  <ListItemText>Finance</ListItemText>
                </ListItemButton>
              </Link>
            </ListItem>
          </>
        )}
        {isEnabled(ALERT_FLAG, false) && (
          <>
            <Divider />
            <ListItem>
              <Link href="/alert">
                <ListItemButton>
                  <ListItemText>Alerts</ListItemText>
                </ListItemButton>
              </Link>
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box className="mobile-header">
      {(["left"] as const).map((anchor) => (
        <React.Fragment key={anchor}>
          <Button
            aria-label="Open navigation menu"
            onClick={toggleDrawer(anchor, true)}
          >
            <SVG.Bar width="30px" />
          </Button>
          <Drawer
            anchor={anchor}
            open={state[anchor]}
            onClose={toggleDrawer(anchor, false)}
          >
            {list(anchor)}
          </Drawer>
        </React.Fragment>
      ))}
    </Box>
  );
}
